import _ from 'lodash';
import { UUIDUtils } from '@utils/UuidUtils';
import { EnvironmentDetail } from '@utils/EnvironmentDetailUtils';
import { CountMetric } from '@src/metrics/CountMetric';
import '@amzn/ihm-the-truth-machine/validator';
import { WeblabService } from '@services//WeblabService';
import { IhmLaborTrackingServiceClient } from '@services/coralservices/serviceClients/IhmLaborTrackingServiceClient';

export interface IBaseDurationEvent {
    Process: string;
    RequestId: string;
    UserAgent?: string;
}

export interface IValidationResult {
    valid: boolean;
    error?: string;
}

const OPERATION_NAME = 'AssociateDuration.recordDuration';
const APPLICATION_NAME = 'IhmFlyingDutchman';

export class AssociateDurationService {
    public static generateBaseEvent(process: string): IBaseDurationEvent {
        return {
            Process: process,
            RequestId: UUIDUtils.generate(),
            UserAgent: EnvironmentDetail.getDeviceType(),
        };
    }

    public static validateMessage<T extends IBaseDurationEvent>(eventMessage: T): IValidationResult {
        const ihmTheTruthMachineJSClient = (window as any).ihmTheTruthMachineJSClient;

        if (!ihmTheTruthMachineJSClient) {
            return { valid: true };
        }

        const eventForValidation = _.extend(
            {
                StoreId: 'placeholder-store-id',
                User: 'placeholder-user',
                StartTimestamp: 0,
                EndTimestamp: 0,
            },
            eventMessage,
        );
        return ihmTheTruthMachineJSClient.validateMessage(eventForValidation);
    }

    public static async recordDurationAndEmitMetric<T extends IBaseDurationEvent>(storeId: string, duration: number, eventMessage: T, eventType: string) {
        try {
            await this.recordDuration(storeId, duration, eventMessage);
            console.debug(`ATBase Recorded ${eventType} duration of ${duration}ms.`);
            const metric = new CountMetric(storeId, `${eventType}.Success.Count`, APPLICATION_NAME, OPERATION_NAME);
            metric.count(1);
        } catch (error) {
            console.error(`ATBase Failed to record ${eventType} duration. Error: ${JSON.stringify(error)}`);
            const metric = new CountMetric(storeId, `${eventType}.Error.Count`, APPLICATION_NAME, OPERATION_NAME);
            metric.count(1);
            throw error;
        } finally {
            const metric = new CountMetric(storeId, `${eventType}.Total.Count`, APPLICATION_NAME, OPERATION_NAME);
            metric.count(1);
        }
    }

    private static async recordDuration<T extends IBaseDurationEvent>(storeId: string, durationInMs: number, eventMessage: T): Promise<void> {
        const validationResult = this.validateMessage(eventMessage);

        if (validationResult.valid) {
            const eventJsonString = JSON.stringify(eventMessage);
            try {
                await IhmLaborTrackingServiceClient.recordAssociateDuration(storeId, durationInMs, eventJsonString);
                console.debug(`ATBase Sent duration event to LTS: storeId = ${storeId}, durationInMs = ${durationInMs}, message = ${eventJsonString}`);
            } catch (error) {
                console.error(`ATBase LTS failed record duration event: ${eventJsonString}, Error message: ${error}`);
                throw error;
            }
        } else {
            throw {
                message: `Invalid duration event. Error: ${validationResult.error}`,
                validationResult,
            };
        }
    }
}
